import React, { useEffect, useState } from "react";
import { MultiSelect } from "primereact/multiselect";
import { BackIcon, CloseIcon } from "../../assets/Icons";
import MachineChart from "../Charts/MachineDistribution";
import { useSelector } from "react-redux";
import { getInitials } from "../../utils/util";
import Skeleton from "react-loading-skeleton";
import ResponseChart from "../Charts/ResponseChart";
import axios from "axios";
import GroupList from "../GroupList";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../../apiClient";
import { GET_GROUPWISE_MACHINE_RESPONSE_STATS, GET_GROUPWISE_RESPONSE_TAG_STATS } from "../../apiClient/endpoints";
import SingleLineBarChart from "../Charts/SingleLineBarChart";
import DateRangeFilterV2 from "./DateRangeFilterV2";
import NegativeResponsesV2 from "./NegativeResponsesV2";
import GroupWiseReportV2 from "./GroupWiseReportV2";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

const DashboardV2 = () => {
  const userData = useSelector((state) => state.userSlice);
  const navigate = useNavigate();
  const { groupId } = useParams();
  const configData = useSelector((state) => state.configSlice);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [machineStatus, setMachineStatus] = useState({
    healthyCount: 0,
    unhealthyCount: 0,
    noRecordsCount: 0,
  });
  const [machineResponseStats, setMachineResponseStats] = useState([]);
  const [options, setOptions] = useState([]);
  const [machineId, setMachineId] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [groupInfo, setGroupInfo] = useState(null);
  const [dates, setDates] = useState({ start: null, end: null });
  const [list, setList] = useState([]);
  const [machineScans, setMachineScans] = useState({});
  const [groupMachineResponseStats, setGroupMachineResponseStats] = useState([]);
  const [negativeTagResponseStats, setNegativeTagResponseStats] = useState([]);

  const machineCount = machineId.length ? machineId.length : options?.length ?? 0;

  const fetchMachineStats = async () => {
    try {
      const { data } = await axios.post(
        `${configData.config.API_URL}/api/dashboard/report/machine/stats/${groupId}`,
        {
          machineIds: machineId.map((item) => item.code),
          dates: {
            start: dates.start ? dates.start.toISOString() : null,
            end: dates.start ? dates.end.toISOString() : null,
          },
        },
        { headers: { Authorization: `Bearer ${userData.token}`, "Content-Type": "application/json" } },
      );
      setMachineStatus(data.data);
    } catch (error) {
      console.error("Error fetching machine stats:", error);
    }
  };

  const fetchMachineList = async () => {
    try {
      const { data } = await axios.get(`${configData.config.API_URL}/api/dashboard/report/list/machine/${groupId}`, {
        headers: { Authorization: `Bearer ${userData.token}`, "Content-Type": "application/json" },
      });
      setOptions(data.data.map((item) => ({ name: item?.label, code: item?.value })));
    } catch (error) {
      console.error("Error fetching machine list:", error);
    }
  };

  const fetchResponseStats = async () => {
    try {
      const { data } = await axios.post(
        `${configData.config.API_URL}/api/dashboard/report/response/stats/${groupId}`,
        {
          machineIds: machineId.map((item) => item.code),
          dates: {
            start: dates.start ? dates.start.toISOString() : null,
            end: dates.start ? dates.end.toISOString() : null,
          },
        },
        { headers: { Authorization: `Bearer ${userData.token}`, "Content-Type": "application/json" } },
      );
      setMachineResponseStats(data.data);
    } catch (error) {
      console.error("Error fetching response stats:", error);
    }
  };

  const fetchGroupDetail = async () => {
    setGroupInfo(null);
    const res = await apiClient.get(`${configData.config.UNO_URL}/api/user/group/details/${groupId}`, {
      headers: { Authorization: `Bearer ${userData?.token}` },
    });
    res ? setGroupInfo(res) : setGroupInfo({});
  };

  const fetchNegativeResponses = async () => {
    try {
      const { data } = await axios.post(
        `${configData.config.API_URL}/api/dashboard/report/negative/responses/${groupId}`,
        {
          machineIds: machineId.map((item) => item.code),
          dates: {
            start: dates.start ? dates.start.toISOString() : null,
            end: dates.start ? dates.end.toISOString() : null,
          },
        },
        { headers: { Authorization: `Bearer ${userData.token}`, "Content-Type": "application/json" } },
      );
      setQuestions(data.data);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const fetchNegativeResponsesGroupwise = async () => {
    try {
      const { data } = await axios.post(
        `${configData.config.API_URL}/api/dashboard/report/negative/responses/byGroup/${groupId}`,
        {
          machineIds: machineId.map((item) => item.code),
          dates: {
            start: dates.start ? dates.start.toISOString() : null,
            end: dates.start ? dates.end.toISOString() : null,
          },
        },
        { headers: { Authorization: `Bearer ${userData.token}`, "Content-Type": "application/json" } },
      );
      setList(data.data);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const fetchMachineScans = async () => {
    const queryParams = {};
    if (dates.start && dates.end) {
      queryParams.start = dates.start.toISOString();
      queryParams.end = dates.end.toISOString();
    }
    if (machineId.length > 0) {
      queryParams.machine = machineId.map((item) => item.code);
    }
    const res = await apiClient.get(`${configData.config.API_URL}/api/dashboard/report/all/responses/${groupId}`, {
      headers: { Authorization: `Bearer ${userData?.token}` },
      params: queryParams,
    });
    setMachineScans(res);
  };

  const fetchGroupWiseMachineResponseStats = async () => {
    try {
      const res = await apiClient.post(`${configData.config.API_URL}${GET_GROUPWISE_MACHINE_RESPONSE_STATS}/${groupId}`, {
        machineIds: machineId.map((item) => item.code),
        dates: {
          start: dates.start ? dates.start.toISOString() : null,
          end: dates.start ? dates.end.toISOString() : null,
        },
      });
      setGroupMachineResponseStats(res);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchNegativeTagResponseStats = async () => {
    try {
      const res = await apiClient.post(`${configData.config.API_URL}${GET_GROUPWISE_RESPONSE_TAG_STATS}/${groupId}`, {
        machineIds: machineId.map((item) => item.code),
        dates: {
          start: dates.start ? dates.start.toISOString() : null,
          end: dates.start ? dates.end.toISOString() : null,
        },
      });
      setNegativeTagResponseStats(() => res);
    } catch (error) {
      console.log(error);
    }
  };

  const Reload = async () => {
    setLoading(true);
    await Promise.all([
      fetchGroupDetail(),
      fetchMachineList(),
      fetchMachineStats(),
      fetchResponseStats(),
      fetchMachineScans(),
      fetchNegativeResponses(),
      fetchNegativeResponsesGroupwise(),
      fetchGroupWiseMachineResponseStats(),
      fetchNegativeTagResponseStats(),
    ]);
    setLoading(false);
  };

  useEffect(() => {
    if (!loading) Reload();
  }, [machineId]);

  useEffect(() => {
    if (!loading) Reload();
  }, [dates]);

  useEffect(() => {
    if (!groupId?.length) return;
    Reload();
  }, [groupId]);

  return (
    <div className="dashboard-v2">
      <div className="dashboard-v2__top">
        <div className="machines-v2__tabs dashboard-v2__tabs">
          {userData?.user?.group?.uuid !== groupId && (
            <button type="button" className="machines-v2__back" onClick={() => navigate(-1)}>
              <BackIcon />
              Back
            </button>
          )}
          <button
            type="button"
            className={`machines-v2__tab ${activeTab === "dashboard" ? "active" : ""}`}
            onClick={() => setActiveTab("dashboard")}
          >
            Dashboard
          </button>
          <button
            type="button"
            className={`machines-v2__tab ${activeTab === "groups" ? "active" : ""}`}
            onClick={() => setActiveTab("groups")}
          >
            Groups
          </button>
        </div>

        {activeTab === "dashboard" && (
          <div className="dashboard-v2__filters">
            <div className="dashboard-v2__machine-select prime-select">
              {machineId?.length > 0 && (
                <span className="dashboard-v2__clear-select" onClick={() => setMachineId([])}>
                  <CloseIcon />
                </span>
              )}
              <MultiSelect
                value={machineId}
                onChange={(e) => setMachineId(e.value)}
                options={options}
                optionLabel="name"
                filter
                placeholder="Select Machines"
                maxSelectedLabels={2}
                className="dashboard-v2__multiselect"
                panelClassName="dashboard-v2__multiselect-panel"
              />
            </div>
            <DateRangeFilterV2 dates={dates} setDates={setDates} />
          </div>
        )}
      </div>

      {activeTab === "groups" ? (
        <div className="machines-v2__groups">
          <GroupList setActiveTab={() => setActiveTab("dashboard")} navigateLink="/dashboard" />
        </div>
      ) : (
        <>
          <div className="dashboard-v2__stats">
            <article className="dashboard-v2__client-card">
              {loading ? (
                <Skeleton width={56} height={56} circle />
              ) : groupInfo?.avatar ? (
                <img src={groupInfo.avatar} alt="" className="dashboard-v2__client-logo" referrerPolicy="no-referrer" />
              ) : (
                <div className="dashboard-v2__client-initials">{groupInfo?.name ? getInitials(groupInfo.name) : ""}</div>
              )}
              <div>
                <strong>{loading ? <Skeleton width={120} /> : groupInfo?.name}</strong>
                <span>Client / Site</span>
              </div>
            </article>

            <article className="sites-v2__stat-card dashboard-v2__mini-stat">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--purple">
                <PrecisionManufacturingOutlinedIcon />
              </div>
              <div>
                <strong>{loading ? "—" : machineCount}</strong>
                <span>Machine Count</span>
              </div>
            </article>

            <article className="sites-v2__stat-card dashboard-v2__mini-stat">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--amber">
                <DescriptionOutlinedIcon />
              </div>
              <div>
                <strong>{loading ? "—" : machineScans?.total ?? 0}</strong>
                <span>Total Scans</span>
              </div>
            </article>

            <article className="sites-v2__stat-card dashboard-v2__mini-stat">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--green">
                <CheckCircleOutlineIcon />
              </div>
              <div>
                <strong>{loading ? "—" : machineScans?.correct ?? 0}</strong>
                <span>Safe to operate Scans</span>
              </div>
            </article>

            <article className="sites-v2__stat-card dashboard-v2__mini-stat">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--red">
                <WarningAmberOutlinedIcon />
              </div>
              <div>
                <strong>{loading ? "—" : machineScans?.incorrect ?? 0}</strong>
                <span>Caution, Trigger Scans</span>
              </div>
            </article>
          </div>

          <div className="dashboard-v2__charts">
            <article className="dashboard-v2__chart-card">
              <h3>Machine&apos;s Response Status</h3>
              {loading ? <Skeleton height={280} /> : <ResponseChart list={machineResponseStats} />}
            </article>
            <article className="dashboard-v2__chart-card">
              <h3>Group Wise Response Status</h3>
              {loading ? <Skeleton height={280} /> : <ResponseChart list={groupMachineResponseStats} />}
            </article>
            <article className="dashboard-v2__chart-card">
              <h3>Machine&apos;s Status &amp; Action Triggers</h3>
              <p className="dashboard-v2__chart-sub">As per recent scans during the selected period!</p>
              {loading ? <Skeleton height={280} /> : <MachineChart machineStatus={machineStatus} />}
            </article>
            <article className="dashboard-v2__chart-card">
              <h3>Negative Tag Response</h3>
              {loading ? (
                <Skeleton height={280} />
              ) : (
                <SingleLineBarChart
                  data={negativeTagResponseStats}
                  xKey="name"
                  yKey="count"
                  chartName="Negative Response"
                />
              )}
            </article>
          </div>

          <NegativeResponsesV2 questions={questions} loading={loading} />
          <GroupWiseReportV2 list={list} loading={loading} />
        </>
      )}
    </div>
  );
};

export default DashboardV2;
